import type { Key, ReactNode } from 'react';
import { cn } from './utils';

export type TableAlign = 'left' | 'center' | 'right';

export interface TableColumn<T> {
  /** Stable id used as React key for headers / cells. */
  id: string;
  header: ReactNode;
  /** Cell renderer. Receives the row and the row index. */
  cell: (row: T, index: number) => ReactNode;
  /** Column alignment. Default left. */
  align?: TableAlign;
  /** Pixel or fraction width applied to the `<col>`. */
  width?: number | string;
  /** Hide on small screens. */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  rows: T[];
  columns: TableColumn<T>[];
  /** Stable key extractor — defaults to row index. */
  rowKey?: (row: T, index: number) => Key;
  /** Compact row height. */
  density?: 'comfortable' | 'compact';
  /** Show top + bottom hairlines + per-row dividers. Default true. */
  divided?: boolean;
  /** Enable hover highlight on rows. */
  hoverable?: boolean;
  /** Empty-state message when `rows` is empty. */
  emptyState?: ReactNode;
  /** Per-row click handler. */
  onRowClick?: (row: T, index: number) => void;
  className?: string;
}

const ALIGN_CLS: Record<TableAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * Table — small, generic, type-safe data table primitive.
 * Uses semantic `<table>` markup and the brand's hairline borders.
 */
export function Table<T>({
  rows,
  columns,
  rowKey,
  density = 'comfortable',
  divided = true,
  hoverable = true,
  emptyState,
  onRowClick,
  className,
}: TableProps<T>) {
  const cellPad = density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';
  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-[12.5px] tabular">
        <colgroup>
          {columns.map((c) => (
            <col key={c.id} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr className={divided ? 'border-b border-ink-100' : ''}>
            {columns.map((c) => (
              <th
                key={c.id}
                scope="col"
                className={cn(
                  'font-semibold text-[10.5px] uppercase tracking-[0.16em] text-ink-500',
                  cellPad,
                  ALIGN_CLS[c.align ?? 'left'],
                  c.hideOnMobile && 'hidden md:table-cell',
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-10 text-center text-[12.5px] text-ink-500"
              >
                {emptyState ?? 'No data.'}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={rowKey ? rowKey(row, i) : i}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                className={cn(
                  divided && 'border-b border-ink-100 last:border-b-0',
                  hoverable && 'hover:bg-ink-50/60 transition-colors',
                  onRowClick && 'cursor-pointer',
                )}
              >
                {columns.map((c) => (
                  <td
                    key={c.id}
                    className={cn(
                      'text-ink-800',
                      cellPad,
                      ALIGN_CLS[c.align ?? 'left'],
                      c.hideOnMobile && 'hidden md:table-cell',
                    )}
                  >
                    {c.cell(row, i)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
